import { useMemo } from "react";
import { Control, useWatch } from "react-hook-form";
import { useParams } from "react-router-dom";
import { useDebounceValue } from "usehooks-ts";

import { SessionInferenceMode } from "@/components/graphql/schema.graphql";
import { GetInferencePricing } from "@/components/inference/queries.graphql";
import * as FormCanvasUtils from "@/components/inference-forms/forms/Canvas/utils";
import { ReferenceMeshType } from "@/components/ui-v2/References/types";
import { usePreflightGated } from "@/hooks/usePreflightGated";
import { useQueryWithResponseHandler } from "@/hooks/useQueryWithResponseHandler";
import { ActionPricing, FileReferenceType, InferenceParameters } from "@/types";
import { convertFormValuesToInferenceCreationParameters } from "@/utils/convertFormValuesToInferenceParameters";

type PricingStrategy =
  | "canvas-create"
  | "canvas-edit"
  | "simple"
  | "source-items";

const MODE_PRICING_STRATEGY: Partial<
  Record<SessionInferenceMode, PricingStrategy>
> = {
  CANVAS_2_D: "canvas-create",
  CANVAS_PROMPT_EDIT: "canvas-edit",
  CREATE: "simple",
  CREATE_3_D: "simple",
  CREATE_SOUND_EFFECT: "simple",
  CREATE_SPEECH: "simple",
  CREATE_VIDEO: "simple",
  REALTIME: "simple",
  PROMPT_EDIT_IMAGE: "source-items",
  PROMPT_EDIT_VIDEO: "source-items",
  UPSCALE: "source-items",
  REMOVE_BACKGROUND: "source-items",
  VECTORIZE_IMAGE: "source-items",
  REFRAME_IMAGE: "source-items",
  UPSCALE_VIDEO: "source-items",
  REFRAME_VIDEO: "source-items",
  REMOVE_BACKGROUND_VIDEO: "source-items",
  ADD_SFX_VIDEO: "source-items",
  RETEXTURE_MESH: "source-items",
  REMESH: "source-items",
  RIG_MESH: "source-items",
};

const getInferencePricingParams = (
  activeMode: SessionInferenceMode | null,
  activeFormValues: any,
): InferenceParameters[] => {
  if (!activeMode) return [];
  const strategy = MODE_PRICING_STRATEGY[activeMode];
  if (!strategy) return [];

  switch (strategy) {
    case "canvas-create":
      return FormCanvasUtils.formatCanvasCreateFormValuesForPricingApi(
        activeFormValues,
      );
    case "canvas-edit":
      return FormCanvasUtils.formatCanvasPromptEditFormValuesForPricingApi(
        activeFormValues,
      );
    case "simple":
      return [
        convertFormValuesToInferenceCreationParameters({
          formValues: activeFormValues,
          mode: activeMode,
        }),
      ];
    case "source-items": {
      const sourceItems = [
        ...(activeFormValues.sourceImages?.map((img: FileReferenceType) => ({
          sourceImage: img,
        })) ?? []),
        ...(activeFormValues.sourceVideos?.map((vid: FileReferenceType) => ({
          sourceVideo: vid,
        })) ?? []),
        ...(activeFormValues.sourceMeshes?.map((mesh: ReferenceMeshType) => ({
          sourceMesh: mesh,
        })) ?? []),
      ];
      if (!sourceItems.length) {
        return [
          convertFormValuesToInferenceCreationParameters({
            formValues: activeFormValues,
            mode: activeMode,
          }),
        ];
      }
      return sourceItems.map((item) =>
        convertFormValuesToInferenceCreationParameters({
          formValues: activeFormValues,
          mode: activeMode,
          ...item,
        }),
      );
    }
  }
};

interface UseInferencePricingProps {
  activeMode: SessionInferenceMode | null;
  // TODO: remove `any` once all forms are migrated to the new composer
  activeForm: { control: Control<any> } | null;
}

export const useInferencePricing = (props: UseInferencePricingProps) => {
  const { activeMode, activeForm } = props;

  const { workspace: workspaceName } = useParams();

  const activeFormValues = useWatch({
    control: activeForm?.control,
  });

  // Debounce a serialized key rather than the raw object: `useWatch` returns a
  // new object reference on every render, and `useDebounceValue` compares with
  // `===`, so debouncing the object resets the timer on every render and never
  // settles while the form re-renders faster than the delay — leaving the
  // pricing spinner spinning indefinitely (a flaky-e2e / real-UX hang). A string
  // key compares by value, so it settles as soon as the form *content* is stable.
  const formValuesKey = useMemo(
    () => JSON.stringify(activeFormValues ?? null),
    [activeFormValues],
  );

  const [debouncedFormValuesKey] = useDebounceValue(formValuesKey, 250);

  // As soon as the debounce starts and we have new form values coming in, we want to toggle loading to true for better UX
  const waitingForDebounce = formValuesKey !== debouncedFormValuesKey;

  const inferencePricingParams = useMemo(() => {
    const debouncedActiveFormValues = debouncedFormValuesKey
      ? JSON.parse(debouncedFormValuesKey)
      : null;
    if (!debouncedActiveFormValues) {
      return null;
    }
    return getInferencePricingParams(activeMode, debouncedActiveFormValues);
  }, [activeMode, debouncedFormValuesKey]);

  const variables = useMemo(() => {
    if (!workspaceName || !inferencePricingParams) {
      return {
        workspaceName: workspaceName ?? "",
        input: { inferenceParams: [] },
      };
    }
    return {
      workspaceName,
      input: {
        inferenceParams: inferencePricingParams,
      },
    };
  }, [workspaceName, inferencePricingParams]);

  const { data, state } = useQueryWithResponseHandler(
    {
      query: GetInferencePricing,
      variables,
      pause: !inferencePricingParams || !variables,
    },
    {
      showSnackbarOnError: false,
    },
  );

  const pricingResult =
    data?.workspace?.__typename === "Workspace"
      ? data.workspace.inferencePricing
      : undefined;
  const actionPrice = pricingResult?.actionPrice ?? null;
  const isGated = usePreflightGated(actionPrice);

  const actionPricing: ActionPricing | undefined = pricingResult
    ? { actionPrice, isGated }
    : undefined;

  return {
    data,
    state,
    actionPricing,
    isFetching: state.fetching || state.stale || waitingForDebounce,
  };
};
